import { create } from "zustand";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import Decimal from "decimal.js";
import { Trading } from "lhc_common";
import { z } from "zod";
import {
    ArrowDown01 as ArrowDown01Icon,
    Delete as DeleteIcon,
    StepForward as StepForwardIcon,
    Dot as DotIcon,
    RotateCcwIcon,
    DownloadCloud,
    Trash2,
    Plus,
    Link as LinkIcon,
    Archive as ArchiveXIcon,
    ListFilter as ListFilterIcon,
    Eye as EyeIcon,
    SquarePen as SquarePenIcon,
    CircleHelp as CircleHelpIcon,
    TriangleAlertIcon,
} from "lucide-react";
import {
  TradingAccountOpenPosition,
  formulateOptionMonths,
  TradingAccount,
  ContractType,
  ContractTypeOptions,
  CommodityOptions,
  Commodities,
  findMatchOption,
  TradingContract,
  UpdateContractValidation,
  CreateContractValidation,
  AccumulatorValidation,
  MinPriceValidation,
  PremiumValidation,
  MinMaxValidation,
  RangeValidation,
  BasePriceReownValidation,
  RangePriceReownValidation,
  RangeWDUValidation,
  ReOwnWDUValidation,
  MinimumPriceValidation,
  MinMaxDoubleUpValidation,
  UpdatePositionValidation,
  DoubleUpValidation,
  HedgePlusValidation,
  RecentReportQueryValidation,
  contractNumberKeys,
  generateAccountLabel,
} from "@/lib/api/otc";
import { Trader, Customer } from "@/lib/api/reports";
import { computeAdjMargin, ignoreSafeParseDecimal, isCanolaMminPrice as checkIsCanolaMminPrice, computeCanolaMinPriceFutures, BearState, PairingData, UpdateContractInfo } from "./OtcBearState";
import { useListTraders } from "@/lib/api/trader";
import { getOptionMonthComboBase, getOptionPredictedExpirationDate, getQuarterlyOptionMonthExpirationDate } from "@/lib/api/common";
import { Skeleton } from "@/components/ui/skeleton";
import {
  formatDateWithoutTimezone,
  formatDateWithoutTimezoneToDate
} from "@/lib/api/date";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { handleFocus } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect } from "@/components/ui/selectMulti";
import { SelectInput } from "@/components/ui/SelectInput";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { Label } from "@/components/ui/label";
import { cn, createDateLabel } from "@/lib/utils";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export const useBearStore = create<BearState>((set) => ({
  open_positions: undefined,
  open_contracts: undefined,
  editing_position_filters: { filters: {} },
  editing_contract_filters: { filters: {} },
  position_filters: { filters: {} },
  contract_filters: { filters: {} },
  new_contract: undefined,
  editing_contract: undefined,
  pairing_data: {},
  hasAttemptedSave: false,
  contracts_filter_modal_status: false,
  positions_filter_modal_status: false,
  contracts_position_modal_contract_id: undefined,
  downloadFilters: undefined,
  hasDownloadsAttemptedSave: false,
  downloadRecentContractFilters: undefined,
  summaryFilters: undefined,
  edit_manual_position_modal_id: undefined,
  margin_report_filters: undefined,

  openContractModalDownloadStatus: undefined,

  edit_manual_position: undefined,
  new_position: undefined,
  hasPositionAttemptedSave: false,

  editDownloadRecentFilterKey: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => {
    return set((state: any) => {
      if (!state.downloadRecentContractFilters) {
        return { downloadRecentContractFilters: undefined };
      }

      if (key === "created_date") {
        return {
          downloadRecentContractFilters: {
            ...state.downloadRecentContractFilters,
            created_range: null,
            [key]: typeof value === "string" ? value?.trim() : value,
          },
        };
      }

      return {
        downloadRecentContractFilters: {
          ...state.downloadRecentContractFilters,
          created_date: null,
          [key]: typeof value === "string" ? value?.trim() : value,
        },
      };
    });
  },
  editDownloadFilterKey: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => {
    return set((state) => {
      if (!state.downloadFilters) {
        return { downloadFilters: undefined };
      }

      return {
        downloadFilters: {
          ...state.downloadFilters,
          [key]: typeof value === "string" ? value?.trim() : value,
        },
      };
    });
  },


  set_margin_report_modal: (new_filters: {} | undefined) => set((state) => ({
    margin_report_filters: new_filters as any,
  })),
  set_edit_manual_position_modal_id: (id: string | undefined) => set((state) => ({
    edit_manual_position_modal_id: id as any,
  })),
  setOpenContractModalDownloadStatus: (new_filters: {} | undefined) => set((state) => ({
    openContractModalDownloadStatus: new_filters as any,
  })),
  setSummaryFilters: (new_filters: {} | undefined) => set((state) => ({
    summaryFilters: new_filters as any,
  })),

  setDownloadFilters: (new_filters: {} | undefined) => set((state) => ({
    downloadFilters: new_filters as any,
    hasDownloadsAttemptedSave: !!new_filters ? state.hasDownloadsAttemptedSave : false,
  })),
  setDownloadRecentContractsFilters: (new_filters?: { created_date: Date } | undefined) => set((state) => ({
    downloadRecentContractFilters: new_filters as any,
  })),
  set_new_position: (new_position: {} | undefined) => set((state) => ({
    new_position: new_position as any,
    hasPositionAttemptedSave: !!new_position ? state.hasPositionAttemptedSave : false,
  })),

  set_edit_manual_position: (edit_position: {} | undefined) => set((state) => ({
    edit_manual_position: edit_position as any,
  })),
  updatePositionAttemptedSave: (status: boolean) => set((state) => ({
    hasPositionAttemptedSave: status,
  })),
  update_margin_report_field: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => {
    return set((state) => {
      if (!state.margin_report_filters) {
        return { margin_report_filters: undefined };
      }

      return {
        margin_report_filters: {
          ...state.margin_report_filters,
          [key]: typeof value === "string" && key !== "contract" ? value?.trim() : value,
        },
      };
    });
  },
  update_edit_position_field: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => {
    return set((state) => {
      if (!state.edit_manual_position) {
        return { edit_manual_position: undefined };
      }

      return {
        edit_manual_position: {
          ...state.edit_manual_position,
          [key]: typeof value === "string" && key !== "contract" ? value?.trim() : value,
        },
      };
    });
  },
  update_new_position_field: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => {
    return set((state) => {
      if (!state.new_position) {
        return { new_position: undefined };
      }

      return {
        new_position: {
          ...state.new_position,
          [key]: typeof value === "string" && key !== "contract" ? value?.trim() : value,
        },
      };
    });
  },

  set_contracts_positions_modal_id: (id?: string | undefined) =>
    set((state) => ({
      contracts_position_modal_contract_id: id,
    })),

  set_open_contracts: (open_contracts: TradingContract[]) =>
    set((state) => ({
      open_contracts,
    })),
  set_open_positions: (open_positions: TradingAccountOpenPosition[]) =>
    set((state) => ({
      open_positions,
    })),
  set_position_filters: (key: string, value: string | undefined | null) =>
    set((state) => ({
      [key]: value,
    })),
  persistPositionFilters: () =>
    set((state) => ({
      positions_filter_modal_status: false,
      position_filters: {
        filters: { ...state.editing_position_filters.filters },
      },
    })),
  persistEditingContractFilters: () =>
    set((state) => ({
      contracts_filter_modal_status: false,
      contract_filters: {
        filters: { ...state.editing_contract_filters.filters },
      },
    })),
  clear_filters: () => set((state) => ({
    contract_filters: { filters: {} },
    position_filters: { filters: {} },
    editing_contract_filters: { filters: {} },
    editing_position_filters: { filters: {} },
  })),
  update_contract_filters_field: (
    key: string,
    value?: string | string[] | number | Date | undefined,
  ) =>
    set((state) => {
      return ({
        editing_contract_filters: {
          filters: {
            ...state.editing_contract_filters.filters,
            [key]: value,
          },
        },
      });
    }),
  update_position_filters_field: (
    key: string,
    value?: string | string[] | number | Date | undefined,
  ) =>
    set((state) => ({
      editing_position_filters: {
        filters: {
          ...state.editing_position_filters.filters,
          [key]: value,
        },
      },
    })),
  set_contract_filter_modal_status: (status: boolean) =>
    set((state) => ({
      contracts_filter_modal_status: status,
    })),
  set_position_filter_modal_status: (status: boolean) =>
    set((state) => ({
      positions_filter_modal_status: status,
    })),
  updateAttemptedSave: (hasAttemptedSave: boolean) =>
    set((state) => ({
      hasAttemptedSave,
    })),
  set_new_contract: (new_contract: {} | undefined) =>
    set((state) => ({
      new_contract: new_contract as any,
      hasAttemptedSave: !!new_contract ? state.hasAttemptedSave : false,
    })),
  set_edit_contract: (editing_contract: {} | undefined) =>
    set((state) => ({
      editing_contract: editing_contract as any,
      hasAttemptedSave: !!editing_contract ? state.hasAttemptedSave : false,
    })),
  set_pair_position: (data: PairingData) =>
    set((state) => ({
      pairing_data: data,
    })),
  update_edit_contract_field: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => {
    return set((state) => {
      if (!state.editing_contract) {
        return { editing_contract: undefined };
      }

      return {
        editing_contract: {
          ...state.editing_contract,
          ...clearFieldsOnContractTypeChange(
            (key === "contract_type"
              ? value
              : state.editing_contract?.contract_type) as any,
          ),
          [key]: key === 'notes' ? value : typeof value === "string" ? value?.trim() : value,
        },
      };
    });
  },
  update_new_contract_field: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => {
    return set((state) => {
      if (!state.new_contract) {
        return { new_contract: undefined };
      }

      return {
        new_contract: {
          ...state.new_contract,
          ...clearFieldsOnContractTypeChange(
            (key === "contract_type"
              ? value
              : state.new_contract?.contract_type) as any,
          ),
          [key]: key === 'notes' ? value : typeof value === "string" ? value?.trim() : value,
        },
      };
    });
  },
}));

function clearFieldsOnContractTypeChange(contract_type?: string) {
  if (!contract_type) return {};
  const type_mapping: Record<string, string[]> = {
    [ContractType.BasePriceReown]: ["step_two", "step_one"],
    [ContractType.Range]: ["step_three", "step_two", "step_one"],
    [ContractType.RangePriceReown]: ["step_three", "step_two", "step_one"],
    [ContractType.RangeWDU]: ["double_up", "step_three", "step_two", "step_one", "option_month_etc_one"],
    [ContractType.ReOwnWDU]: ["step_three", "step_two", "step_one", "option_month_etc_one"],
    [ContractType.MinMax]: ["step_three", "step_one"],
    [ContractType.HedgePlus]: ["step_one"],
    [ContractType.MinMaxWDU]: ["step_three", "step_two", "step_one", "option_month_etc_one"],
    [ContractType.PremiumOfferWDU]: [
      "step_three",
      "otc_futures",
      "futures_price",
      "account_no_b",
      "option_month_etc_one",
    ],
    [ContractType.DoubleUp]: [
      "step_three",
      "option_month_etc_one",
    ],
    [ContractType.MinPriceCallSpread]: ["step_one", "step_three"],
    [ContractType.MinimumPrice]: ["step_three"],
    [ContractType.AccWeeklyDU]: [
      "start_date",
      "weeks",
      "period_qty",
      "knockout",
      "step_one",
      "option_month_etc_one",
    ],
  };

  if (!type_mapping[contract_type]) return {};

  const clear_values = Object.entries(type_mapping).reduce((acc, entry) => {
    const [key, values] = entry;
    if (key === contract_type) return acc;

    values.forEach((valKey: string) => {
      acc = {
        ...acc,
        [valKey]: undefined,
      } as any;
    });

    return acc;
  }, {});

  type_mapping[contract_type].forEach((key: string) => {
    delete (clear_values as any)[key];
  });

  return clear_values;
}

export function ContractForm({
  isPreviewOnly,
  accounts,
  customers,
  isUpdateForm,
  onEditField,
  onSubmit,
}: {
  isPreviewOnly?: boolean;
  accounts: TradingAccount[];
  customers: Customer[];
  isUpdateForm: boolean;
  onEditField: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => void;
  onSubmit: (formData: UpdateContractInfo) => Promise<boolean>;
}) {
  const new_contract_data = useBearStore((state) => state.new_contract);
  const editing_contract_data = useBearStore((state) => state.editing_contract);
  const hasAttemptedSave = useBearStore((state) => state.hasAttemptedSave);
  const listTradersRes = useListTraders();

  const fields = (
    isUpdateForm ? editing_contract_data : new_contract_data
  ) as any;

  let doesDifferExpiration =  false;
  let expectedDate = undefined;
  if (fields && fields.option_month && fields.end_date) {
    const expectedEndDate = fields.end_date;
    try {
      const option_month = fields.option_month;
      const optionMonthResult = option_month ? getOptionMonthComboBase(option_month) : undefined;
      const new_expiration_date = Trading.Dates.Expiration.calculateLastTradingDay(optionMonthResult!.firstDayOfMonthMoment);
      expectedDate = new_expiration_date;
      if (!!expectedEndDate && !!new_expiration_date) {
        doesDifferExpiration = !moment(expectedEndDate).endOf("day").isSame((moment(new_expiration_date).endOf("day")));
      }
    } catch(error) {
    }
  }

  if (!fields) return null;

  if ([listTradersRes].some((result) => result.isLoading)) {
    return (
      <div className="flex flex-col space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    );
  }

  const traders = listTradersRes?.data?.data || [];

  const margin = ignoreSafeParseDecimal(fields.contract_fee || 0).sub(
    ignoreSafeParseDecimal(fields.option_premium || 0),
  );
  const margin_amount = margin.times(
    ignoreSafeParseDecimal(fields.quantity || 0),
  );

  const {
    adj_margin, adj_margin_amount,
    adj_margin_two, adj_margin_amount_two,
    adj_margin_three, adj_margin_amount_three,
  } = computeAdjMargin(fields);

  const isCanolaMminPriceContract = checkIsCanolaMminPrice(fields);

  const { expected_contract_futures: expectedContractFutures, futures_margin: futuresMarginComputed } = isCanolaMminPriceContract
    ? computeCanolaMinPriceFutures(fields)
    : { expected_contract_futures: new Decimal(0), futures_margin: new Decimal(0) };

  const isSubmittingDisabled = !isFormValid(isUpdateForm, fields);

  const OTCAccountTypeOptions = createAccountListOptions(undefined)(accounts);
  const HTAAccountTypeOptions = createAccountListOptions(fields.contract_type || "")(accounts);

  const schema = getSchemaForType(isUpdateForm, fields.contract_type);
  const TraderOptions = traders.map((trader: Trader) => ({
    value: trader.id,
    label: trader.name || "",
  }));
  const rowStepColCount = [
    5,
    ([ContractType.PremiumOfferWDU, ContractType.RangeWDU].includes(fields.contract_type)) ? 1 : 0,
    fields.commodity === Commodities.Canola ? 1 : 0,
  ].reduce((acc, item) => acc + item, 0);

  return (
    <form
      aria-disabled={isPreviewOnly}
      onSubmit={(e) => {
        if (e?.preventDefault) e.preventDefault();
        if (isPreviewOnly) return;
        return onSubmit(fields as any);
      }}
    >
      <div className="flex flex-col md:flex md:flex-col items-start gap-4 content-center">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 col-span-1 sm:col-span-2 md:col-span-5 content-center md:w-full ">
          <div className="grid items-center gap-1.5 content-center">
            <BearDatePicker
              tabIndex={1}
              name="date"
              label="Date"
              required
              disabled={isPreviewOnly}
              value={fields.date}
              onValueChange={(date) => onEditField("date", date)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
          <div className="grid items-center gap-1.5 content-center">
            <BearDatePicker
              tooltip={(doesDifferExpiration) ? (
                <Tooltip>
                    <TooltipTrigger><TriangleAlertIcon className="h-4 w-4 text-orange-400" /></TooltipTrigger>
                    <TooltipContent>
                        <p>Expected an end date of {moment(expectedDate).format("MM/DD/YYYY")}</p>
                    </TooltipContent>
                </Tooltip>
              ) : null}
              required
              tabIndex={1}
              name="end_date"
              label="Expiration Date"
              disabled={isPreviewOnly}
              value={fields.end_date}
              onValueChange={(date) => onEditField("end_date", date)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
          <div className="grid items-center gap-1.5 content-center">
            <BearSelectInput
              tabIndex={1}
              name="contract_type"
              label="Contract Type"
              required
              disabled={isPreviewOnly}
              value={fields.contract_type || ""}
              onValueChange={(id) => {
                return onEditField("contract_type", id);
              }}
              options={ContractTypeOptions}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>

          <div className="grid items-center gap-1.5">
            <BearSelectInput
              tabIndex={2}
              name="customer_id"
              label="Customer"
              required
              disabled={isPreviewOnly}
              value={fields.customer_id || ""}
              onValueChange={(id) => onEditField("customer_id", id)}
              options={customers.map((customer: Customer) => ({
                value: customer.id,
                label: customer.long_name,
              }))}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
          <div className="grid items-center gap-1.5 content-center">
            <BearSelectInput
              required
              tabIndex={3}
              name="commodity"
              label="Commodity"
              disabled={isPreviewOnly}
              value={fields.commodity || ""}
              onValueChange={(id) => {
                const commodity = findMatchOption(CommodityOptions, id)?.value;
                if (id && fields.option_month) {
                  try {
                    const optionMonthResult = getOptionMonthComboBase(fields.option_month);
                    const new_expiration_date = Trading.Dates.Expiration.calculateLastTradingDay(optionMonthResult.firstDayOfMonthMoment);

                    onEditField(
                      "end_date",
                      new_expiration_date,
                    );
                  } catch (error) { }
                }
                
                // Clear implied_fx_amount and canola min price fields if commodity changes from canola to non-canola
                if (fields.commodity === Commodities.Canola && commodity !== Commodities.Canola) {
                  onEditField("implied_fx_amount", undefined);
                  onEditField("actual_contract_futures", undefined);
                }
                
                return onEditField(
                  "commodity",
                  commodity,
                );
              }

              }
              options={CommodityOptions}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 col-span-1 sm:col-span-2 md:grid-cols-5 md:col-span-5 content-center md:w-full">
          <div className="grid items-center gap-1.5">
            <BearSelectInput
              tabIndex={5}
              name="option_month"
              label="OTC Opt. Month"
              required
              disabled={isPreviewOnly}
              value={fields.option_month || ""}
              onValueChange={(value) => {
                const optionMonth = findMatchOption(formulateOptionMonths(fields.option_month), value)?.value;
                if (!!optionMonth) {
                  try {
                    const optionMonthResult = value ? getOptionMonthComboBase(optionMonth) : undefined;

                    const new_expiration_date = Trading.Dates.Expiration.calculateLastTradingDay(optionMonthResult!.firstDayOfMonthMoment);
                    onEditField(
                      "end_date",
                      new_expiration_date,
                    );
                  } catch(error) {
                    console.error("otc option month error value change", error)
                  }
                }
                return onEditField("option_month", optionMonth)
              }
              }
              options={formulateOptionMonths(fields.option_month)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
          <BearIntInput
            tabIndex={6}
            name="quantity"
            label="Quantity"
            required
            step="5000"
            disabled={isPreviewOnly}
            value={fields.quantity}
            onChange={(value) => onEditField("quantity", value)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />
          <div className="grid items-center gap-1.5 content-center">
            <BearInput
              tabIndex={7}
              name="trade_reference"
              label="Trade Reference"
              disabled={isPreviewOnly}
              value={fields.trade_reference || ""}
              onChange={(event) =>
                onEditField("trade_reference", event.target.value)
              }
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
          <div className="grid items-center gap-1.5 content-center">
            <BearSelectInput
              tabIndex={8}
              name="account_no_a"
              label="Brokerage OTC"
              required
              disabled={isPreviewOnly}
              value={fields.account_no_a || ""}
              onValueChange={(id) => {
                return onEditField("account_no_a", id);
              }}
              options={OTCAccountTypeOptions}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
        </div>

        <div
          className={cn(
            "grid items-center gap-1.5 gap-4 content-center",
            "grid-cols-1 sm:grid-cols-2 md:grid-cols-4 col-span-1 sm:col-span-2 md:col-span-5 md:col-span-5 md:w-full",
            `md:grid-cols-6 md:col-span-6`
          )}
        >
          {[
            ContractType.MinMax,
            ContractType.Range,
            ContractType.RangePriceReown,
            ContractType.BasePriceReown,
            ContractType.RangeWDU,
            ContractType.MinPriceCallSpread,
            ContractType.AccWeeklyDU,
            ContractType.MinMaxWDU,
            ContractType.ReOwnWDU,
            ContractType.HedgePlus,
          ].includes(fields.contract_type as any) ? (
              <div className="grid items-center gap-1.5 content-center">
                <BearFloatInput
                  required={!isUpdateForm}
                  tabIndex={9}
                  name="step_one"
                  label="Step 1"
                  disabled={isPreviewOnly}
                  value={fields.step_one}
                  onChange={(value) => onEditField("step_one", value)}
                  schema={schema}
                  hasAttemptedSave={hasAttemptedSave}
                />
              </div>
            ) : (<span />)}
          {[
            ContractType.Range, ContractType.RangeWDU, ContractType.MinMaxWDU, ContractType.ReOwnWDU,
            ContractType.BasePriceReown,
            ContractType.RangePriceReown,
          ].includes(fields.contract_type as any) ? (
            <div className="grid items-center gap-1.5 content-center">
              <BearFloatInput
                required={!isUpdateForm}
                tabIndex={10}
                name="step_two"
                label="Step 2"
                disabled={isPreviewOnly}
                value={fields.step_two}
                onChange={(value) => onEditField("step_two", value)}
                schema={schema}
                hasAttemptedSave={hasAttemptedSave}
              />
            </div>
          ) : (<span />)}
          {[
            ContractType.MinMax,
            ContractType.Range,
            ContractType.RangePriceReown,
            ContractType.RangeWDU,
            ContractType.MinPriceCallSpread,
            ContractType.MinimumPrice,
            ContractType.PremiumOfferWDU,
            ContractType.DoubleUp,
            ContractType.MinMaxWDU,
            ContractType.ReOwnWDU,
          ].includes(fields.contract_type as any) ? (
              <div className="grid items-center gap-1.5 content-center">
                <BearFloatInput
                  required={!isUpdateForm}
                  tabIndex={11}
                  name="step_three"
                  label="Step 3"
                  disabled={isPreviewOnly}
                  value={fields.step_three}
                  onChange={(value) => onEditField("step_three", value)}
                  schema={schema}
                  hasAttemptedSave={hasAttemptedSave}
                />
              </div>
            ) : (<span />)}
          {[
            ContractType.RangeWDU,
          ].includes(fields.contract_type as any) ? (
              <div className="grid items-center gap-1.5 content-center">
                <BearFloatInput
                  required={!isUpdateForm}
                  tabIndex={11}
                  name="double_up"
                  label="Double Up"
                  disabled={isPreviewOnly}
                  value={fields.double_up}
                  onChange={(value) => onEditField("double_up", value)}
                  schema={schema}
                  hasAttemptedSave={hasAttemptedSave}
                />
              </div>
          ) : (<span />)}
          {fields.commodity === Commodities.Canola ? (
          <BearFloatInput
            required
            tabIndex={11}
            name="implied_fx_amount"
            label="Implied FX Amount"
            step="0.0001"
            disabled={isPreviewOnly}
            value={fields.implied_fx_amount}
            onChange={(value) => onEditField("implied_fx_amount", value)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />
          ) : (<span />)}
          </div>
        {isCanolaMminPriceContract && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 col-span-1 sm:col-span-2 md:col-span-3 content-center md:w-full">
            <BearFloatInput
              name="expected_contract_futures"
              label="Expected Contract Futures"
              disabled
              value={expectedContractFutures.isZero() ? "" : expectedContractFutures.toFixed(6)}
              onChange={() => {}}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              tabIndex={11}
              name="actual_contract_futures"
              label="Actual Contract Futures"
              disabled={isPreviewOnly}
              value={fields.actual_contract_futures}
              onChange={(value) => onEditField("actual_contract_futures", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              name="futures_margin"
              label="Futures Margin"
              disabled
              value={futuresMarginComputed.isZero() ? "" : futuresMarginComputed.toFixed(2)}
              onChange={() => {}}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
        )}
        <div
          className={cn(
            "grid items-center gap-1.5 gap-4 content-center",
            "grid-cols-1 sm:grid-cols-2 md:grid-cols-4 col-span-1 sm:col-span-2 md:col-span-4 md:w-full",
            ([ContractType.PremiumOfferWDU, ContractType.RangeWDU].includes(fields.contract_type))
              ? "md:grid-cols-5 md:col-span-5"
              : "",
          )}
        >
          {[ContractType.PremiumOfferWDU].includes(
            fields.contract_type as any,
          ) && (
              <>
                <div className="grid items-center gap-1.5 content-center">
                  <BearSelectInput
                    tabIndex={11}
                    name="account_no_b"
                    label="Brokerage HTA"
                    required
                    disabled={isPreviewOnly}
                    value={fields.account_no_b || ""}
                    onValueChange={(id) => {
                      return onEditField("account_no_b", id);
                    }}
                    options={HTAAccountTypeOptions}
                    schema={schema}
                    hasAttemptedSave={hasAttemptedSave}
                  />
                </div>
                <div className="grid items-center gap-1.5 content-center">
                  <BearFloatInput
                    tabIndex={11}
                    required
                    name="futures_price"
                    label="Futures Price"
                    disabled={isPreviewOnly}
                    value={fields.futures_price}
                    onChange={(value) => onEditField("futures_price", value)}
                    schema={schema}
                    hasAttemptedSave={hasAttemptedSave}
                  />
                </div>
                <div className="grid items-center gap-1.5 content-center">
                  <BearFloatInput
                    tabIndex={11}
                    required
                    name="otc_futures"
                    label="OTC Futures"
                    disabled={isPreviewOnly}
                    value={fields.otc_futures}
                    onChange={(value) => onEditField("otc_futures", value)}
                    schema={schema}
                    hasAttemptedSave={hasAttemptedSave}
                  />
                </div>
              </>
            )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 col-span-1 sm:col-span-2 md:grid-cols-6 md:col-span-6 content-center md:w-full">
          <div className="grid items-center gap-1.5 content-center">
            <span className="flex flex-row">
              <BearFloatInput
                className="grow"
                tabIndex={12}
                required
                name="option_premium"
                label="Option Premium"
                disabled={isPreviewOnly}
                value={fields.option_premium}
                onChange={(value) => onEditField("option_premium", value)}
                schema={schema}
                hasAttemptedSave={hasAttemptedSave}
              />
              <HoverCard>
                <HoverCardTrigger asChild>
                  <CircleHelpIcon className="ml-4 w-4" />
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="flex justify-between space-x-4">
                    <div className="space-y-1">
                      <p className="text-sm flex flex-row">
                        <DotIcon />{" "}Option premium is (+) if you paid funds for option
                      </p>
                      <div className="flex items-center pt-2">
                        <span className="text-sm flex flex-row">
                          <DotIcon />{" "}Option Premium is (-) If you collected funds for option
                        </span>
                      </div>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </span>
          </div>
          <div className="grid items-center gap-1.5 content-center">
            <BearFloatInput
              tabIndex={13}
              required
              name="contract_fee"
              label="Initial Premium + Fee"
              disabled={isPreviewOnly}
              value={fields.contract_fee}
              onChange={(value) => onEditField("contract_fee", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
          <div className="grid items-center gap-1.5 content-center">
            <BearFloatInput
              required
              name="margin"
              label="Lighthouse Fee"
              disabled
              value={margin.toString()}
              onChange={(value) => onEditField("margin", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
          <div className="grid items-center gap-1.5 content-center">
            <BearFloatInput
              required
              name="margin_amount"
              label="Margin Amount"
              disabled
              value={margin_amount.toString()}
              onChange={(value) => onEditField("margin_amount", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          </div>
          <BearInput
            name="contract_no"
            label="Contract #"
            tabIndex={13}
            disabled={isPreviewOnly}
            value={fields.contract_no || ""}
            onChange={(event) =>
              onEditField("contract_no", event.target.value)
            }
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />
          <BearInput
            name="contract_reference"
            label="Contract Reference"
            required={[
              ContractType.BasePriceReown,
              ContractType.RangePriceReown,
              ContractType.ReOwnWDU,
            ].includes(fields.contract_type as any)}
            tabIndex={13}
            disabled={isPreviewOnly}
            value={fields.contract_reference || ""}
            onChange={(event) =>
              onEditField("contract_reference", event.target.value)
            }
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 col-span-1 sm:col-span-2 md:col-span-5 content-center  md:w-full">
            <BearFloatInput
              className="grow"
              tabIndex={14}
              name="adj_option_premium"
              label="Adj Option Premium"
              disabled={isPreviewOnly}
              value={fields.adj_option_premium?.toString()}
              onChange={(value) => onEditField("adj_option_premium", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              tabIndex={14}
              name="adj_contract_fee"
              label="Adj Initial Premium + Fee"
              disabled={isPreviewOnly}
              value={fields.adj_contract_fee?.toString()}
              onChange={(value) => onEditField("adj_contract_fee", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              name="adj_margin"
              label="Adj Lighthouse Fee"
              tabIndex={14}
              disabled
              value={(adj_margin)?.toString()}
              onChange={(value) => onEditField("adj_margin", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              name="adj_margin_amount"
              label="Adj Margin Amount"
              disabled
              value={adj_margin_amount.toString()}
              onChange={(value) => onEditField("adj_margin_amount", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          <BearDatePicker
            tabIndex={14}
            name="adjusted_date"
            label="Adjusted Date"
            disabled={isPreviewOnly}
            value={fields.last_adjusted_date}
            onValueChange={(date) => onEditField("last_adjusted_date", date)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 col-span-1 sm:col-span-2 md:col-span-5 content-center md:w-full">
            <BearFloatInput
              className="grow"
              tabIndex={14}
              name="adj_option_premium_two"
              label="Adj Option Premium 2"
              disabled={isPreviewOnly}
              value={fields.adj_option_premium_two?.toString()}
              onChange={(value) => onEditField("adj_option_premium_two", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              tabIndex={14}
              name="adj_contract_fee_two"
              label="Adj Initial Premium + Fee 2"
              disabled={isPreviewOnly}
              value={fields.adj_contract_fee_two?.toString()}
              onChange={(value) => onEditField("adj_contract_fee_two", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              name="adj_margin_two"
              label="Adj Lighthouse Fee 2"
              tabIndex={14}
              disabled
              value={(adj_margin_two)?.toString()}
              onChange={(value) => onEditField("adj_margin_two", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              name="adj_margin_amount_two"
              label="Adj Margin Amount 2"
              disabled
              value={adj_margin_amount_two.toString()}
              onChange={(value) => onEditField("adj_margin_amount_two", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          <BearDatePicker
            tabIndex={14}
            name="adjusted_date_two"
            label="Adjusted Date 2"
            disabled={isPreviewOnly}
            value={fields.adjusted_date_two}
            onValueChange={(date) => onEditField("adjusted_date_two", date)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 col-span-1 sm:col-span-2 md:col-span-5 content-center md:w-full">
            <BearFloatInput
              className="grow"
              tabIndex={14}
              name="adj_option_premium_three"
              label="Adj Option Premium 3"
              disabled={isPreviewOnly}
              value={fields.adj_option_premium_three?.toString()}
              onChange={(value) => onEditField("adj_option_premium_three", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              tabIndex={14}
              name="adj_contract_fee_three"
              label="Adj Initial Premium + Fee 3"
              disabled={isPreviewOnly}
              value={fields.adj_contract_fee_three?.toString()}
              onChange={(value) => onEditField("adj_contract_fee_three", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              name="adj_margin_three"
              label="Adj Lighthouse Fee 3"
              tabIndex={14}
              disabled
              value={(adj_margin_three)?.toString()}
              onChange={(value) => onEditField("adj_margin_three", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
            <BearFloatInput
              name="adj_margin_amount_three"
              label="Adj Margin Amount 3"
              disabled
              value={adj_margin_amount_three?.toString()}
              onChange={(value) => onEditField("adj_margin_amount_three", value)}
              schema={schema}
              hasAttemptedSave={hasAttemptedSave}
            />
          <BearDatePicker
            tabIndex={14}
            name="adjusted_date_three"
            label="Adjusted Date 3"
            disabled={isPreviewOnly}
            value={fields.adjusted_date_three}
            onValueChange={(date) => onEditField("adjusted_date_three", date)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />
        </div>
        {[ContractType.AccWeeklyDU].includes(fields.contract_type as any) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 col-span-1 sm:col-span-2 md:col-span-4 content-center">
            <div className="grid items-center gap-1.5 content-center">
              <BearDatePicker
                required
                tabIndex={14}
                name="start_date"
                label="Start Date"
                disabled={isPreviewOnly}
                value={fields.start_date}
                onValueChange={(date) => onEditField("start_date", date)}
                schema={schema}
                hasAttemptedSave={hasAttemptedSave}
              />
            </div>
            <div className="grid items-center gap-1.5 content-center">
              <BearFloatInput
                tabIndex={16}
                required
                name="weeks"
                label="Weeks"
                disabled={isPreviewOnly}
                value={fields.weeks}
                onChange={(value) => onEditField("weeks", value)}
                schema={schema}
                hasAttemptedSave={hasAttemptedSave}
              />
            </div>
            <div className="grid items-center gap-1.5 content-center">
              <BearFloatInput
                tabIndex={17}
                required
                name="period_qty"
                label="Period Qty"
                disabled={isPreviewOnly}
                value={fields.period_qty}
                onChange={(value) => onEditField("period_qty", value)}
                schema={schema}
                hasAttemptedSave={hasAttemptedSave}
              />
            </div>
            <div className="grid items-center gap-1.5 content-center">
              <BearFloatInput
                tabIndex={18}
                required
                name="knock_out"
                label="Knockout"
                disabled={isPreviewOnly}
                value={fields.knockout}
                onChange={(value) => onEditField("knockout", value)}
                schema={schema}
                hasAttemptedSave={hasAttemptedSave}
              />
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 col-span-1 sm:col-span-2 md:grid-cols-5 md:col-span-5 content-center  md:w-full">
          <BearDatePicker
            required
            tabIndex={19}
            name="shipment_start_date"
            label="Shipment Start Date"
            disabled={isPreviewOnly}
            value={fields.shipment_start_date}
            onValueChange={(date) => onEditField("shipment_start_date", date)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />
          <BearDatePicker
            required
            tabIndex={20}
            name="shipment_end_date"
            label="Shipment End Date"
            disabled={isPreviewOnly}
            value={fields.shipment_end_date}
            onValueChange={(date) => onEditField("shipment_end_date", date)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />
          <BearSelectInput
            required
            tabIndex={21}
            name="contract_trader_options"
            label="Trader"
            disabled={isPreviewOnly}
            value={fields.trader_id || ""}
            onValueChange={(value) =>
              onEditField(
                "trader_id",
                findMatchOption(TraderOptions, value)?.value,
              )
            }
            options={TraderOptions}
          />
            <BearIntInput
            tabIndex={22}
            name="crop_year"
            label="Crop Year"
            required
            step="1"
            disabled={isPreviewOnly}
            value={fields.crop_year}
            onChange={(value) => onEditField("crop_year", value)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
            />
            <BearSelectInput
            tabIndex={23}
            name="rolled_option_month"
            label="Futures Rolled Month"
            disabled={isPreviewOnly}
            value={fields.rolled_option_month || ""}
            onValueChange={(value) =>
                onEditField("rolled_option_month", findMatchOption(formulateOptionMonths(fields.rolled_option_month), value)?.value,)
            }
            options={formulateOptionMonths(fields.rolled_option_month)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
            />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 col-span-1 sm:col-span-2 md:grid-cols-5 md:col-span-5 content-center  md:w-full">
        <BearFloatInput
          tabIndex={23}
          name="carry"
          label="Futures Carry"
          disabled={isPreviewOnly}
          value={fields.carry}
          onChange={(value) => onEditField("carry", value)}
          schema={schema}
          hasAttemptedSave={hasAttemptedSave}
        />
        <BearSelectInput
          tabIndex={24}
          name="rolled_basis_month"
          label="Basis Rolled Month"
          disabled={isPreviewOnly}
          value={fields.rolled_basis_month || ""}
          onValueChange={(value) =>
            onEditField("rolled_basis_month", findMatchOption(formulateOptionMonths(fields.rolled_basis_month), value)?.value,)
          }
          options={formulateOptionMonths(fields.rolled_basis_month)}
          schema={schema}
          hasAttemptedSave={hasAttemptedSave}
        />
        <BearFloatInput
          tabIndex={25}
          name="basis_carry"
          label="Basis Carry"
          disabled={isPreviewOnly}
          value={fields.basis_carry}
          onChange={(value) => onEditField("basis_carry", value)}
          schema={schema}
          hasAttemptedSave={hasAttemptedSave}
        />
        <BearInput
          name="notes"
          label="Notes"
          tabIndex={26}
          disabled={isPreviewOnly}
          value={fields.notes || ""}
          onChange={(event) =>
            onEditField("notes", event.target.value)
          }
          schema={schema}
          hasAttemptedSave={hasAttemptedSave}
        />
        {[
          ContractType.PremiumOfferWDU,
          ContractType.RangeWDU,
          ContractType.DoubleUp,
          ContractType.AccWeeklyDU,
          ContractType.MinMaxWDU,
          ContractType.ReOwnWDU,
        ].includes(fields.contract_type as any) && (
            <div className="grid items-center gap-1.5 content-center">
              <BearSelectInput
                tabIndex={27}
                name="option_month_etc_one"
                label="HTA DU Opt. Month"
                required
                disabled={isPreviewOnly}
                value={fields.option_month_etc_one || ""}
                onValueChange={(value) =>
                  onEditField("option_month_etc_one", findMatchOption(formulateOptionMonths(fields.option_month), value)?.value)
                }
                options={formulateOptionMonths(fields.option_month_etc_one)}
                schema={schema}
                hasAttemptedSave={hasAttemptedSave}
              />
            </div>
          )}
        </div>



        <div className="grid grid-cols-1 place-content-between gap-2 col-span-1 sm:col-span-2 md:col-span-4 md:w-full">
          <div className="flex flex-row justify-end gap-4">
            <DialogClose asChild>
              <Button type="button" variant="secondary" className="col-start-1">
                Close
              </Button>
            </DialogClose>

            {!isPreviewOnly && (
              <Button
                type="submit"
                size="sm"
                className="px-3 col-start-4"
                disabled={isSubmittingDisabled}
              >
                {isUpdateForm ? "Update" : "Create"}
              </Button>

            )}
          </div>
        </div>
      </div>
    </form>
  );
}


export function isFormValid(isUpdateForm: boolean, formData: any) {
  const data = JSON.parse(JSON.stringify(transformToApiContract(formData)));
  const schema = getSchemaForType(isUpdateForm, data.contract_type);


  if (!isUpdateForm && !schema.safeParse(data).success) {
    return false;
  }

  if (isUpdateForm && !schema.safeParse(data).success) {
    return false;
  }

  // Additional check: implied_fx_amount is required for canola
  if (data.commodity === Commodities.Canola && (!data.implied_fx_amount || data.implied_fx_amount.trim() === "")) {
    return false;
  }

  return true;
}

export function generateTotalMargin(openContracts: TradingContract[]) {
  const amount = openContracts.reduce((acc, contract) => {
    return acc.plus(contract.margin_amount);
  }, new Decimal(0));
  const currencyAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  })
    .format(amount.abs().toNumber())
    .replace("$", "");
  return amount.isNegative() ? `$ (${currencyAmount})` : `$ ${currencyAmount}`;
}

export function getSchemaForType(isUpdateForm: boolean, contract_type: string) {
  switch (contract_type) {
    case ContractType.BasePriceReown:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(BasePriceReownValidation)
        : BasePriceReownValidation;
    case ContractType.Range:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(RangeValidation)
        : RangeValidation;
    case ContractType.RangePriceReown:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(RangePriceReownValidation)
        : RangePriceReownValidation;
    case ContractType.RangeWDU:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(RangeWDUValidation)
        : RangeWDUValidation;
    case ContractType.MinMaxWDU:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(MinMaxDoubleUpValidation)
        : MinMaxDoubleUpValidation;
    case ContractType.HedgePlus:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(HedgePlusValidation)
        : HedgePlusValidation;
    case ContractType.MinMax:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(MinMaxValidation)
        : MinMaxValidation;
    case ContractType.PremiumOfferWDU:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(PremiumValidation)
        : PremiumValidation;
    case ContractType.DoubleUp:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(DoubleUpValidation)
        : DoubleUpValidation;
    case ContractType.MinPriceCallSpread:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(MinPriceValidation)
        : MinPriceValidation;
    case ContractType.MinimumPrice:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(MinimumPriceValidation)
        : MinimumPriceValidation;
    case ContractType.AccWeeklyDU:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(AccumulatorValidation)
        : AccumulatorValidation;
    case ContractType.ReOwnWDU:
      return isUpdateForm
        ? transformSchemaToOptionalNullish(ReOwnWDUValidation)
        : ReOwnWDUValidation;
    default:
      return RangeValidation;
  }
}

export function transformSchemaToOptionalNullish(schema: any) {
  return schema.merge(z.object({
    step_one: z.string().min(1).optional(),
    step_two: z.string().min(1).optional(),
    step_three: z.string().min(1).optional(),
    double_up: z.string().min(1).optional(),
  }));
}

export function transformGenericApiData(item: Record<string, any>): any {
  return Object.entries(item).reduce(
    (acc, [key, value]) => {
      if (value === "") {
        acc[key] = undefined;
      } else if (!!value && key?.toLocaleLowerCase()?.includes("date") && !/createdby|updatedby|id/i.test(key ?? "")) {
        if (typeof value === "object") {
          acc[key] = formatDateWithoutTimezoneToDate(value);
        } else {
          acc[key] = formatDateWithoutTimezoneToDate(new Date(value));
        }
      } else if (value === null) {
        acc[key] = undefined;
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>,
  );
}


export function transformToApiContract(item: Record<string, any>): any {
  if (!!item["commodity"]) {
    item["commodity"] = item["commodity"]?.toLocaleUpperCase();
  }

  return Object.entries(item).reduce(
    (acc, [key, value]) => {
      if (value === "") {
        acc[key] = undefined;
      } else if (typeof value === "number" && key === "crop_year") {
        acc[key] = value;
      } else if (
        typeof value === "number" ||
        contractNumberKeys.includes(key)
      ) {
        try {
          acc[key] = new Decimal(value);
        } catch (error: any) {
          acc[key] = undefined;
        }
      } else if (!!value && (key?.toLocaleLowerCase()?.includes("date") && !/createdby|updatedby|id/i.test(key ?? ""))) {
        if (typeof value === "object") {
          acc[key] = formatDateWithoutTimezoneToDate(value);
        } else {
          acc[key] = formatDateWithoutTimezoneToDate(new Date(value));
        }
      } else if (value === null) {
        acc[key] = undefined;
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>,
  );
}

export function createAccountListOptions(contract_type: ContractType | undefined) {
  return function createAccountListHelper(accountsList: TradingAccount[]) {
    return accountsList.filter((account: TradingAccount) => {
      switch (contract_type || "") {
        case ContractType.PremiumOfferWDU:
          return account.is_futures || false;
        case ContractType.DoubleUp:
          return account.is_futures || false;
        default:
          return account.is_otc || account.is_futures;
      }

    }).map((account: TradingAccount) => ({
      value: account.id,
      label: generateAccountLabel(account.account_id) || account.account_id,
    }));
  };
}


export function BearSelectMultiInput({
  values,
  onValueChange,
  options,
  name,
  required,
  label,
}: {
  values: string[];
  onValueChange: any;
  options: { value: string; label: string }[];
  name: string;
  required?: boolean;
  label?: string;
}) {
  return (
    <div className="grid items-center gap-1.5">
      {label && (
        <Label
          htmlFor={name}
          className="flex flex-row justify-between text-base items-center"
        >
          <span className="font-semibold">{label}</span>
          {required ? (
            <span
              className="text-xs font-light"
              style={{ fontStyle: "italic" }}
            >
              Required
            </span>
          ) : (
            <span></span>
          )}
        </Label>
      )}
      <MultiSelect
        values={values}
        setValues={onValueChange}
        options={options}
        listClassName="overflow-y-scroll max-h-44 min-h-36"
      />
    </div>
  );
}

export function BearSelectInput({
  value,
  onValueChange,
  options,
  name,
  required,
  label,
  tabIndex,
  schema,
  hasAttemptedSave,
  disabled,
}: {
  value: string;
  onValueChange: (item: string) => void;
  options: { value: string; label: string }[];
  name: string;
  required?: boolean;
  label?: string;
  tabIndex?: number;
  schema?: any;
  hasAttemptedSave?: boolean;
  disabled?: boolean;
}) {
  const data = JSON.parse(
    JSON.stringify(transformToApiContract({ [name]: value })),
  );
  const hasErrors = schema
    ? !(schema as any).pick({ [name]: true }).safeParse(data).success
    : false;

  return (
    <div className="grid items-center gap-1.5">
      {label && (
        <Label
          htmlFor={name}
          className="flex flex-row justify-between text-base items-center"
        >
          <span className="font-semibold">{label}</span>
          {required ? (
            <span
              className="text-xs font-light"
              style={{ fontStyle: "italic" }}
            >
              Required
            </span>
          ) : (
            <span></span>
          )}
        </Label>
      )}

      <Combobox
        tabIndex={tabIndex}
        isModal
        className="w-full"
        value={value}
        disabled={disabled}
        onValueChange={(item: any) => {
          if (!!value && item?.toLocaleLowerCase() === value?.toLocaleLowerCase()) {
            return onValueChange(undefined as any);
          }

          return onValueChange(item);
        }}
        options={options}
      />
    </div>
  );
}

export function BearDatePicker({
  tooltip,
  value,
  onValueChange,
  name,
  required,
  label,
  tabIndex,
  schema,
  hasAttemptedSave,
  disabled,
}: {
  tooltip?: any;
  value: Date | undefined;
  onValueChange: (date: Date | undefined) => void;
  name: string;
  required?: boolean;
  label?: string;
  tabIndex?: number;
  schema?: any;
  hasAttemptedSave?: boolean;
  disabled?: boolean;
}) {
  const data = JSON.parse(
    JSON.stringify(transformToApiContract({ [name]: value })),
  );
  const hasErrors = schema
    ? !(schema as any).pick({ [name]: true }).safeParse(data).success
    : false;

  return (
    <div className="grid items-center gap-1.5">
      {label && (
        <Label
          htmlFor={name}
          className="flex flex-row justify-between text-base items-center"
        >
          <span className="font-semibold">{label}</span>
          {!!tooltip && (tooltip)}
          {required ? (
            <span
              className="text-xs font-light"
              style={{ fontStyle: "italic" }}
            >
              Required
            </span>
          ) : (
            <span></span>
          )}
        </Label>
      )}
      <DatePicker
        tabIndex={tabIndex}
        value={value}
        disabled={disabled}
        onChange={(date) => {
          return onValueChange(date);
        }}
        placeholder={"MM/DD/YYYY"}
        className={cn(
          "rounded-md border-0 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6 w-full",
          hasAttemptedSave && hasErrors
            ? "ring-red-500 focus:ring-red-500"
            : "",
        )}
      />
    </div>
  );
}

export function BearFloatInput({
  className,
  disabled,
  value,
  onChange,
  name,
  min,
  max,
  required,
  step,
  label,
  tabIndex,
  schema,
  hasAttemptedSave,
}: {
  className?: string;
  value: string;
  onChange: (item: string | number) => void;
  name: string;
  min?: string;
  max?: string;
  required?: boolean;
  step?: string;
  label?: string;
  disabled?: boolean;
  tabIndex?: number;
  schema?: any;
  hasAttemptedSave?: boolean;
}) {
  const data = JSON.parse(
    JSON.stringify(transformToApiContract({ [name]: value })),
  );

  const passesPattern = value
    ? /^-?\d*\.?\d+$/.test(value ? `${value}` : "")
    : true;
  const hasErrors = schema
    ? !(schema as any).pick({ [name]: true }).safeParse(data).success ||
    !passesPattern
    : false;

  return (
    <div className={cn("grid items-center gap-1.5", className)}>
      {label && (
        <Label
          htmlFor={name}
          className="flex flex-row justify-between text-base items-center"
        >
          <span className="font-semibold">{label}</span>
          {required ? (
            <span
              className="text-xs font-light"
              style={{ fontStyle: "italic" }}
            >
              Required
            </span>
          ) : (
            <span></span>
          )}
        </Label>
      )}
      <Input
        tabIndex={tabIndex}
        disabled={disabled}
        value={value ? `${value}` : ""}
        id={name}
        type="text"
        onChange={(event: any) =>
          onChange(event.target.value?.replace(/[^\-0-9\.\$\(\) ]+/gim, ""))
        }
        className={cn(
          "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6",
          hasAttemptedSave && hasErrors
            ? "ring-red-500 focus:ring-red-500"
            : "",
        )}
        onFocus={handleFocus}
      />
    </div>
  );
}

export function BearIntInput({
  disabled,
  placeholder,
  value,
  onChange,
  name,
  min,
  max,
  required,
  step,
  label,
  tabIndex,
  schema,
  hasAttemptedSave,
}: {
  disabled?: boolean;
  placeholder?: string;
  value: number;
  onChange: (item: number) => void;
  name: string;
  min?: string;
  max?: string;
  required?: boolean;
  step?: string;
  label?: string;
  tabIndex?: number;
  schema: any;
  hasAttemptedSave: boolean;
}) {
  const data = JSON.parse(
    JSON.stringify(transformToApiContract({ [name]: value })),
  );
  const hasErrors = !(schema as any).pick({ [name]: true }).safeParse(data)
    .success;

  return (
    <div className="grid items-center gap-1.5">
      {label && (
        <Label
          htmlFor={name}
          className="flex flex-row justify-between text-base items-center"
        >
          <span className="font-semibold">{label}</span>
          {required ? (
            <span
              className="text-xs font-light"
              style={{ fontStyle: "italic" }}
            >
              Required
            </span>
          ) : (
            <span></span>
          )}
        </Label>
      )}
      <Input
        disabled={disabled}
        value={value}
        id={name}
        type="number"
        required={required}
        min={min}
        max={max}
        step={step}
        onChange={parseIntEvent(onChange)}
        className={cn(
          "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6",
          hasAttemptedSave && hasErrors
            ? "ring-red-500 focus:ring-red-500"
            : "",
        )}
        onFocus={handleFocus}
        tabIndex={tabIndex}
        placeholder={placeholder}
      />
    </div>
  );
}

export function BearInput({
  placeholder,
  value,
  onChange,
  name,
  required,
  label,
  tabIndex,
  className,
  schema,
  hasAttemptedSave,
  disabled,
}: {
  placeholder?: string;
  value: string;
  onChange: (event: any) => void;
  name: string;
  required?: boolean;
  label?: string;
  tabIndex?: number;
  className?: string;
  schema?: any;
  hasAttemptedSave?: boolean;
  disabled?: boolean;
}) {
  const data = JSON.parse(
    JSON.stringify(transformToApiContract({ [name]: value })),
  );
  const hasErrors = schema
    ? !(schema as any).pick({ [name]: true }).safeParse(data).success
    : false;

  return (
    <div className="grid items-center gap-1.5">
      {label && (
        <Label
          htmlFor={name}
          className="flex flex-row justify-between text-base items-center"
        >
          <span className="font-semibold">{label}</span>
          {required ? (
            <span
              className="text-xs font-light"
              style={{ fontStyle: "italic" }}
            >
              Required
            </span>
          ) : (
            <span></span>
          )}
        </Label>
      )}
      <Input
        value={value}
        id={name}
        type="text"
        required={required}
        disabled={disabled}
        onChange={onChange}
        className={cn(
          "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6",
          hasAttemptedSave && hasErrors
            ? "ring-red-500 focus:ring-red-500"
            : "",
        )}
        onFocus={handleFocus}
        tabIndex={tabIndex}
        placeholder={placeholder}
      />
    </div>
  );
}

export function parseIntEvent(onChange: (item: number) => void) {
  return function parseIntEventHelper(event: any) {
    try {
      return onChange(parseInt(event.target.value));
    } catch (e) {
      return onChange(0);
    }
  };
}

export function parseFloatNumber(onChange: (item: string | number) => void) {
  return function parseFloatEventHelper(event: any) {
    try {
      return onChange(parseFloat(event.target.value || ""));
    } catch (e) {
      return onChange(event.target.value || "");
    }
  };
}
